import React, { useMemo } from 'react';
import {
  ObjectionBubbleCanvas,
  ObjectionChoroplethMap,
  ObjectionDispositionRangeCanvas,
  ObjectionHeatmapTable
} from './ObjectionDashboardCharts';
import KPICard from './KPICard';





/**
 * Renders /api/objections/dashboard payload (charts + KPIs).
 */
const ObjectionDashboardPanel = ({ data, loading, error }) => {
  const charts = data?.charts;
  const kpis = data?.kpis;
  const insights = data?.insights;
  const meta = data?.meta;
  const deltas = data?.deltas;

  const heatmapMax = charts?.heatmap?.max_value || 0;
  const stateRows = charts?.heatmap?.state_rows || [];
  const toiCols = charts?.heatmap?.toi_columns || [];

  const heatLookup = useMemo(() => {
    const m = {};
    const matrix = charts?.heatmap?.matrix || [];
    matrix.forEach((cell) => {
      m[`${cell.state}|${cell.toi}`] = Number(cell.objection_count || 0);
    });
    return m;
  }, [charts]);

  const ranking = charts?.state_ranking || [];
  const disposition = useMemo(() => charts?.disposition_ranges_by_state || [], [charts]);
  const bubbles = charts?.toi_bubbles || [];
  const mapByState = charts?.map_by_state || [];

  const hasDispositionRanges = useMemo(
    () => disposition.some((r) => r.min_disposition_days != null && r.max_disposition_days != null),
    [disposition]
  );

  if (loading) {
    return (
      <div className="report-card" style={{ padding: '24px', marginBottom: '16px' }}>
        Loading objection dashboard…
      </div>
    );
  }
  if (error) {
    return (
      <div className="report-card" style={{ padding: '16px', marginBottom: '16px', borderColor: 'var(--orange-bg)', background: 'var(--orange-bg)' }}>
        <strong>Dashboard error:</strong> {error}
      </div>
    );
  }
  if (!data || !kpis) {
    return (
      <div className="report-card" style={{ padding: '24px', marginBottom: '16px', color: 'var(--text3)' }}>
        No dashboard data returned.
      </div>
    );
  }

  const topStates = insights?.highestObjectionStates?.top || [];

  return (
    <div className="objection-dashboard-root report-scope" style={{ display: 'grid', gap: '20px', background: 'transparent' }}>
      {meta?.date_from && meta?.date_to && (
        <div style={{ fontSize: '12px', color: 'var(--text3)' }}>
          Range: {meta.date_from} — {meta.date_to}
          {meta.prior_period ? ` · Prior comparison: ${meta.prior_period.from} — ${meta.prior_period.to}` : ''}
        </div>
      )}
      <div className="kpi-banner-grid" style={{ marginBottom: 0 }}>
        {[
          { label: 'FILINGS W/ OBJECTIONS', value: kpis.filings_with_objections, sub: kpis.filings_with_objections > 0 ? `${(kpis.total_objections / kpis.filings_with_objections).toFixed(1)} avg obj / filing` : 'No current objections' },
          { label: 'TOTAL OBJECTIONS', value: kpis.total_objections, sub: deltas?.totalObjectionsPct != null ? `${deltas.totalObjectionsPct >= 0 ? '+' : ''}${deltas.totalObjectionsPct}% vs prior` : null },
          { label: 'STATES', value: `${kpis.states_represented} / ${kpis.states_total_us || 50}`, sub: topStates.length > 0 ? `Top: ${topStates.map((s) => s.state_abbr || s.state).join(' & ')}` : 'Active jurisdictions' },
          { label: 'AVG. DISPOSITION (DAYS)', value: kpis.avg_disposition_days ?? '—', sub: deltas?.avgDispositionDaysDelta != null ? `${deltas.avgDispositionDaysDelta >= 0 ? '+' : ''}${deltas.avgDispositionDaysDelta} vs prior` : null },
          { label: 'UNANSWERED OBJECTIONS', value: kpis.unanswered_objections, sub: kpis.total_objections > 0 ? `${Math.round((kpis.unanswered_objections / kpis.total_objections) * 100)}% of total pool` : 'No open items' }
        ].map((k) => (
          <KPICard
            key={k.label}
            title={k.label}
            value={k.value}
            subtitle={k.sub}
          />
        ))}
      </div>

      {/* <div className="kpi-banner-grid" style={{ marginBottom: 0 }}>
          <div className="kpi-banner-card" style={{ borderLeft: '3px solid var(--c-orange)', height: 'auto', padding: '14px' }}>
            <div className="kpi-banner-title">TOP OBJECTION STATES</div>
            <div className="kpi-banner-value" style={{ fontSize: '18px', fontFamily: 'monospace' }}>
              {topStates.map((s) => s.state_abbr || s.state).join(' & ') || '—'}
            </div>
            <div className="kpi-banner-subtitle" style={{ marginTop: '4px', color: 'var(--text2)' }}>
              {share != null ? `${share}% of objections in this scope.` : 'Not enough geographic spread.'}
              {themes.length > 0 ? ` Themes: ${themes.slice(0, 3).map((t) => `${t.category} (${t.count})`).join(', ')}.` : ''}
            </div>
          </div>

          <div className="kpi-banner-card" style={{ borderLeft: '3px solid var(--c-orange)', height: 'auto', padding: '14px' }}>
            <div className="kpi-banner-title">SLOWEST TOI (AVG DISPOSITION)</div>
            <div className="kpi-banner-value" style={{ fontSize: '18px', fontFamily: 'monospace' }}>
              {slowToi?.avg_disposition_days != null ? `${slowToi.avg_disposition_days}d` : '—'}
            </div>
            <div className="kpi-banner-subtitle" style={{ marginTop: '4px', color: 'var(--text2)' }}>
              {slowToi ? (
                <>
                  <div title={slowToi.toi}>{slowToi.toi}</div>
                  <div style={{ marginTop: '4px' }}>
                    {slowToi.filing_count} filing{slowToi.filing_count === 1 ? '' : 's'} · avg{' '}
                    {slowToi.avg_objections_per_filing} obj/filing
                  </div>
                </>
              ) : (
                'Need disposition dates on filings.'
              )}
            </div>
          </div>

          <div className="kpi-banner-card" style={{ borderLeft: '3px solid var(--c-teal-active)', height: 'auto', padding: '14px' }}>
            <div className="kpi-banner-title">OBJECTION COUNT VS PRIOR WINDOW</div>
            <div className="kpi-banner-value" style={{ fontSize: '18px', fontFamily: 'monospace' }}>
              {trend?.change_pct != null ? `${trend.change_pct >= 0 ? '+' : ''}${trend.change_pct}%` : '—'}
            </div>
            <div className="kpi-banner-subtitle" style={{ marginTop: '4px', color: 'var(--text2)' }}>
              {trend ? `Current ${trend.current_total_objections} vs prior ${trend.prior_total_objections}.` : 'Set a date range on the dashboard request for trend.'}
            </div>
          </div>
        </div> */}
      <ObjectionHeatmapTable
        stateRows={stateRows}
        toiCols={toiCols}
        heatLookup={heatLookup}
        heatmapMax={heatmapMax}
      />

      <div className="report-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid var(--border)', paddingBottom: '10px' }}>
          <div className="report-card-title" style={{ margin: 0, border: 'none', padding: 0 }}>Geographical Distribution & State Rankings</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '24px', alignItems: 'start' }}>
          <div style={{ minHeight: '380px', padding: '10px' }}>
            <ObjectionChoroplethMap mapByState={mapByState} />
          </div>
          <div style={{ padding: '4px 0' }}>
            <div style={{ marginBottom: '16px', borderBottom: '1px solid var(--border)', paddingBottom: '6px' }}>
              <div style={{ fontSize: '14px', color: 'var(--c-black)', letterSpacing: '0.6px' }}>Active Region Rankings</div>
            </div>
            {(() => {
              const displayRanking = ranking.slice(0, 20);
              if (displayRanking.length === 0) {
                return <div style={{ color: 'var(--text3)', textAlign: 'center', padding: '40px 0' }}>No active states for this metric</div>;
              }
              const maxVal = Number(displayRanking[0]?.objection_count) || 1;

              return displayRanking.map((r) => {
                const val = Number(r.objection_count) || 0;
                const barColor = '#1c866a';
                const pct = (val / maxVal) * 100;

                return (
                  <div key={r.state} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
                    <div style={{ width: '32px', fontSize: '15px', fontWeight: 600, color: 'var(--c-black)', fontFamily: 'monospace' }}>
                      {r.state_abbr || r.state}
                    </div>
                    <div style={{ flex: 1, height: '6px', position: 'relative', background: 'var(--c-white)', borderRadius: '3px', overflow: 'hidden' }}>
                      <div
                        className="map-bar-animate"
                        style={{
                          width: `${pct}%`,
                          height: '100%',
                          background: barColor,
                          borderRadius: '3px'
                        }}
                      />
                    </div>
                    <div style={{ width: '70px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: 'var(--text2)' }}>
                      {val} total
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </div>

      <div className="report-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
          <div className="report-card-title" style={{ margin: 0, border: 'none', padding: 0 }}>State-Wise Disposition Days — Min, Avg, Max</div>
          {/* <span style={{ fontFamily: 'monospace', fontSize: '10px', color: 'var(--text3)', background: 'var(--c-white)', padding: '2px 8px', borderRadius: '99px', border: '1px solid var(--border)' }}>
            range plot
          </span> */}
        </div>
        {!hasDispositionRanges ? (
          <span style={{ color: 'var(--text3)', fontSize: '13px', lineHeight: 1.5 }}>
            No filings with submission and disposition dates on snapshots yet (pending filings have no disposition), or dates use an unsupported format.
          </span>
        ) : (
          <ObjectionDispositionRangeCanvas disposition={disposition} />
        )}
      </div>
      <div className="report-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', borderBottom: '1px solid var(--border)', paddingBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
          <div className="report-card-title" style={{ margin: 0, border: 'none', padding: 0 }}>
            TOI — Avg Objections vs Avg Disposition{' '}
            <span style={{ fontSize: '12px', fontWeight: 400, color: 'var(--text3)' }}>(bubble size = filing count)</span>
          </div>
        </div>
        <ObjectionBubbleCanvas bubbles={bubbles} />
      </div>


    </div>
  );
};

export default ObjectionDashboardPanel;
